import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Buildings, MapPin, CheckCircle, XCircle, Handshake, Envelope, Sparkle, MusicNotes, SealCheck, Clock, PaperPlaneTilt } from '@phosphor-icons/react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import MultiSelectDropdown from '@/components/MultiSelectDropdown';

export default function LabelPortal() {
  const [claims, setClaims] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [showClaim, setShowClaim] = useState(false);
  const [labels, setLabels] = useState([]);
  const [labelFilters, setLabelFilters] = useState({ genres: [] });
  const [claimForm, setClaimForm] = useState({
    mode: 'existing',
    label_id: '',
    name: '',
    genres: [],
    location: '',
    bio: '',
    website: '',
    social_links: {
      instagram: '',
      facebook: '',
      twitter: '',
      soundcloud: '',
    },
    spotify_id: '',
    youtube_id: '',
    soundcloud_id: '',
    role_note: '',
    verification_method: 'OFFICIAL_EMAIL',
    official_email: '',
    evidence_url: '',
    public_email: '',
  });
  const [claimSent, setClaimSent] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [respondFor, setRespondFor] = useState(null); // {demo, label}
  const [respForm, setRespForm] = useState({ decision: 'interested', feedback: '' });
  const [aiWriting, setAiWriting] = useState(false);
  const [savingFreeCredits, setSavingFreeCredits] = useState({});

  const load = () => Promise.all([
    api.get('/labels/my-claims').then(r => setClaims(r.data)),
    api.get('/demos/inbox').then(r => setInbox(r.data)),
    api.get('/labels').then(r => setLabels(r.data)),
    api.get('/labels/filters').then(r => setLabelFilters(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const resetClaimForm = () => ({
    mode: 'existing',
    label_id: '',
    name: '',
    genres: [],
    location: '',
    bio: '',
    website: '',
    social_links: {
      instagram: '',
      facebook: '',
      twitter: '',
      soundcloud: '',
    },
    spotify_id: '',
    youtube_id: '',
    soundcloud_id: '',
    role_note: '',
    verification_method: 'OFFICIAL_EMAIL',
    official_email: '',
    evidence_url: '',
    public_email: '',
  });

  const updateClaimForm = (key, value) => {
    setClaimForm(prev => ({ ...prev, [key]: value }));
  };

  const updateClaimSocial = (key, value) => {
    setClaimForm(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [key]: value,
      },
    }));
  };

  const doClaim = async () => {
    if (claimForm.mode === 'existing') {
      if (!claimForm.label_id) {
        toast.error('Pick an existing label');
        return;
      }
    } else {
      if (!claimForm.name.trim()) {
        toast.error('Enter the label name');
        return;
      }
    }

    setClaiming(true);
    try {
      const onboardingPayload = claimForm.mode === 'existing'
        ? {
            label_id: claimForm.label_id,
            name: labels.find(l => l.label_id === claimForm.label_id)?.name || '',
            role_note: claimForm.role_note.trim() || undefined,
          }
        : {
            name: claimForm.name.trim(),
            genre: claimForm.genres[0] || undefined,
            genres: claimForm.genres.length ? claimForm.genres : undefined,
            location: claimForm.location.trim() || undefined,
            bio: claimForm.bio.trim() || undefined,
            website: claimForm.website.trim() || undefined,
            social_links: Object.fromEntries(
              Object.entries(claimForm.social_links)
                .map(([key, value]) => [key, value.trim()])
                .filter(([, value]) => value)
            ),
            spotify_id: claimForm.spotify_id.trim() || undefined,
            youtube_id: claimForm.youtube_id.trim() || undefined,
            soundcloud_id: claimForm.soundcloud_id.trim() || undefined,
            role_note: claimForm.role_note.trim() || undefined,
          };

      const { data } = await api.post('/labels/onboarding', onboardingPayload);
      const claim = data.claim;

      setClaimSent({
        ...data,
        claim,
        dns: data.dns || null,
      });

      toast.success(
        data.created
          ? 'Label created and claim submitted'
          : 'Label claim submitted'
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start label verification');
    } finally {
      setClaiming(false);
    }
  };

  const submitVerification = async () => {
    if (!claimSent?.claim?.claim_id) {
      toast.error('Start the label claim first');
      return;
    }

    const method = claimForm.verification_method;

    if (method === 'OFFICIAL_EMAIL' && !claimForm.official_email.trim()) {
      toast.error('Enter the official label email');
      return;
    }

    if (method === 'PUBLIC_EMAIL') {
      if (!claimForm.evidence_url.trim()) {
        toast.error('Enter the public page URL');
        return;
      }
      if (!claimForm.public_email.trim()) {
        toast.error('Enter the public label email');
        return;
      }
    }

    setClaiming(true);
    try {
      const { data } = await api.post('/labels/claims/evidence', {
        claim_id: claimSent.claim.claim_id,
        method,
        evidence_url:
          method === 'PUBLIC_EMAIL'
            ? claimForm.evidence_url.trim()
            : undefined,
        public_email:
          method === 'OFFICIAL_EMAIL'
            ? claimForm.official_email.trim()
            : method === 'PUBLIC_EMAIL'
              ? claimForm.public_email.trim()
              : undefined,
      });

      setClaimSent(prev => ({
        ...prev,
        claim: data.claim,
      }));

      toast.success('Verification passed. Your claim is now waiting for TuneAmplifier review.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setClaiming(false);
    }
  };

  const closeClaim = () => {
    setShowClaim(false);
    setClaimSent(null);
    setClaimForm(resetClaimForm());
  };

  const updateLabelFreeCredits = async (labelId, checked) => {
    setSavingFreeCredits(prev => ({
      ...prev,
      [labelId]: true,
    }));

    try {
      const { data } = await api.put(`/labels/${labelId}/settings`, {
        accept_free_credits: checked === true,
      });

      setClaims(prev =>
        prev.map(claim =>
          claim.label_id === labelId
            ? {
                ...claim,
                label: {
                  ...(claim.label || {}),
                  accept_free_credits: Boolean(data.accept_free_credits),
                },
              }
            : claim
        )
      );

      toast.success(
        checked === true
          ? 'Free-credit submissions enabled for this label.'
          : 'Free-credit submissions disabled for this label.'
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          'Could not update free-credit submission settings'
      );
    } finally {
      setSavingFreeCredits(prev => ({
        ...prev,
        [labelId]: false,
      }));
    }
  };

  const openRespond = (demo, lbl, decision = 'interested') => {
    setRespondFor({ demo, label: lbl });
    setRespForm({ decision, feedback: '' });
  };

  const writeFeedback = async () => {
    if (!respondFor) return;
    setAiWriting(true);
    try {
      const song = respondFor.demo.song;
      const chat = await api.post('/chat/sessions', { model: 'gpt-terra', memory: false, title: 'A&R feedback' });
      const chatId = chat.data.chat_id;
      // Fire streaming request; collect text via fetch
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/sessions/${chatId}/message`, {
        method: 'POST', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('session_token') ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` } : {}),
        },
        body: JSON.stringify({
          text: `Write a short (2-3 sentence), warm but honest A&R feedback message to an artist for a "${respForm.decision}" decision on their song "${song?.title || 'untitled'}" (${song?.genre || ''}) by ${song?.artist || 'artist'}. No emoji. First person, from a label rep. Do not include a greeting or sign-off.`,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '', full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const line = buffer.slice(0, idx).trim(); buffer = buffer.slice(idx + 2);
          if (!line.startsWith('data:')) continue;
          try { const e = JSON.parse(line.slice(5).trim()); if (e.delta) full += e.delta; } catch {}
        }
      }
      // clean up chat session
      api.delete(`/chat/sessions/${chatId}`).catch(() => {});
      setRespForm(prev => ({ ...prev, feedback: full.trim() }));
    } catch (e) { toast.error('AI write failed'); }
    finally { setAiWriting(false); }
  };

  const respond = async () => {
    if (!respondFor) return;
    try {
      await api.post(`/demos/${respondFor.demo.demo_id}/respond`, {
        label_id: respondFor.label.label_id,
        decision: respForm.decision,
        feedback: respForm.feedback || undefined,
      });
      toast.success(`Response sent · artist notified`);
      setRespondFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const claimedIds = new Set(claims.map(c => c.label_id));
  const availableLabels = labels.filter(l => !claimedIds.has(l.label_id));
  const verifiedClaims = claims.filter(
    c => c.verified || c.claim_status === 'APPROVED' || c.status === 'APPROVED'
  );
  const pendingClaims = claims.filter(
    c => !c.verified && c.claim_status !== 'APPROVED' && c.status !== 'APPROVED'
  );

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Label Response Portal</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Sign your next artist.</h1>
          <p className="text-zinc-400 mt-1 text-sm max-w-2xl">Claim a label you represent, review incoming demos, and respond with AI-assisted feedback. Artists get notified automatically.</p>
        </div>
        <Button data-testid="claim-label-btn" onClick={() => setShowClaim(true)} className="btn-neon rounded-full h-10 font-display">+ Claim label</Button>
      </header>

      {/* Claims strip */}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider mb-2">Labels you represent</div>
        {claims.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-xl bg-zinc-900/20 p-6 text-sm text-zinc-500 font-mono">
            No labels claimed yet — click "Claim label" and verify your work email to start receiving demos.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {claims.map(c => (
              <div key={c.label_id} data-testid={`claim-${c.label_id}`} className={`border rounded-full pl-1 pr-3 py-1 flex items-center gap-2 ${
                c.verified ? 'border-[#ccff00]/40 bg-[#ccff00]/5' : 'border-amber-500/40 bg-amber-500/5'
              }`}>
                <img src={c.label?.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                <span className="text-sm font-medium">{c.label?.name}</span>
                {c.verified ? (
                  <span data-testid={`claim-verified-${c.label_id}`} className="text-[10px] font-mono uppercase text-[#ccff00] flex items-center gap-1"><SealCheck size={12} weight="fill" /> verified</span>
                ) : (
                  <span data-testid={`claim-pending-${c.label_id}`} className="text-[10px] font-mono uppercase text-amber-400 flex items-center gap-1"><Clock size={12} /> awaiting email</span>
                )}
                {c.role_note && <span className="text-[10px] font-mono text-zinc-500">· {c.role_note}</span>}
                {c.verified && (
                  <div className="flex items-center gap-2 ml-2 pl-2 border-l border-zinc-800">
                    <Checkbox
                      id={`label-free-credits-${c.label_id}`}
                      checked={Boolean(c.label?.accept_free_credits)}
                      disabled={Boolean(savingFreeCredits[c.label_id])}
                      onCheckedChange={(checked) =>
                        updateLabelFreeCredits(c.label_id, checked)
                      }
                    />
                    <label
                      htmlFor={`label-free-credits-${c.label_id}`}
                      className="text-[10px] font-mono text-zinc-400 cursor-pointer whitespace-nowrap"
                    >
                      Accept free credits
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {pendingClaims.length > 0 && (
          <div data-testid="pending-verify-note" className="mt-3 text-xs font-mono text-amber-400/80 border border-amber-500/20 bg-amber-500/5 rounded-lg p-3">
            {pendingClaims.length} claim{pendingClaims.length > 1 ? 's' : ''} waiting on email verification — check your label inbox for the TuneAmplifier magic link (valid 30 min).
          </div>
        )}
      </div>

      <Tabs defaultValue="inbox" className="tabs-underline">
        <TabsList className="bg-transparent border-b border-zinc-800 rounded-none w-full justify-start h-auto p-0 gap-6 mb-4">
          <TabsTrigger data-testid="tab-portal-inbox" value="inbox" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">Inbox ({inbox.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="space-y-3">
          {inbox.length === 0 && (
            <div className="text-sm text-zinc-500 py-6">
              {claims.length === 0 ? 'Claim a label first to see incoming demos.' : verifiedClaims.length === 0 ? 'Verify your work email to unlock the demo inbox.' : 'No demos received yet for the labels you represent.'}
            </div>
          )}
          {inbox.map(d => (
            <div key={d.demo_id} data-testid={`inbox-demo-${d.demo_id}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center shrink-0">
                  <MusicNotes size={24} className="text-[#ccff00]" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg">{d.song?.title} <span className="text-zinc-500 font-normal text-sm">by {d.song?.artist}</span></div>
                  <div className="text-xs font-mono text-zinc-500">
                    From {d.artist?.name} <Envelope size={10} className="inline" /> {d.artist?.email} · {new Date(d.created_at).toLocaleString()}
                  </div>
                  {d.pitch && <p className="text-sm text-zinc-400 mt-2 italic">"{d.pitch}"</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {d.relevant_labels?.map(l => {
                      const r = d.responses?.[l.label_id] || { status: 'pending' };
                      return (
                        <div key={l.label_id} className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${
                          r.status === 'pending' ? 'border-zinc-800 bg-zinc-950' : 'border-[#ccff00]/30 bg-[#ccff00]/5'
                        }`}>
                          <img src={l.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                          <span className="text-xs font-medium">{l.name}</span>
                          {r.status === 'pending' ? (
                            <div className="flex items-center gap-1 ml-2">
                              <button data-testid={`interested-${d.demo_id}-${l.label_id}`} onClick={() => openRespond(d, l, 'interested')} className="text-xs font-mono text-[#ccff00] hover:underline flex items-center gap-1"><CheckCircle size={12} /> Interested</button>
                              <span className="text-zinc-700">·</span>
                              <button data-testid={`signed-${d.demo_id}-${l.label_id}`} onClick={() => openRespond(d, l, 'signed')} className="text-xs font-mono text-[#ccff00] hover:underline flex items-center gap-1"><Handshake size={12} /> Sign</button>
                              <span className="text-zinc-700">·</span>
                              <button data-testid={`pass-${d.demo_id}-${l.label_id}`} onClick={() => openRespond(d, l, 'passed')} className="text-xs font-mono text-zinc-500 hover:text-red-400 flex items-center gap-1"><XCircle size={12} /> Pass</button>
                            </div>
                          ) : (
                            <span className="text-[10px] font-mono uppercase text-[#ccff00] ml-2">{r.status}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Claim dialog */}
      <Dialog open={showClaim} onOpenChange={(v) => (v ? setShowClaim(true) : closeClaim())}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Claim or add a label</DialogTitle>
          </DialogHeader>

          {claimSent ? (
            <div className="space-y-5" data-testid="claim-sent-panel">
              <div className="border border-zinc-800 bg-zinc-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <SealCheck size={20} className="text-[#ccff00]" weight="fill" />
                  <span className="font-display text-lg">
                    {claimSent.label?.name || 'Label'} — verification
                  </span>
                </div>
                <div className="text-xs font-mono text-zinc-500 mt-2">
                  Status: <span className="text-zinc-300">{claimSent.claim?.status || 'PENDING_VERIFICATION'}</span>
                </div>
              </div>

              {claimSent.claim?.status === 'PENDING_VERIFICATION' && (
                <>
                  {claimSent.dns?.record_name && claimSent.dns?.token && (
                    <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 rounded-lg p-4 space-y-2">
                      <div className="font-display">DNS verification</div>
                      <p className="text-xs text-zinc-400">
                        Add a TXT record to your label domain, then click Verify DNS.
                      </p>
                      <div className="text-xs font-mono space-y-1">
                        <div><span className="text-zinc-500">Record:</span> {claimSent.dns.record_name}</div>
                        <div className="break-all"><span className="text-zinc-500">Value:</span> {claimSent.dns.token}</div>
                      </div>
                      <Button
                        data-testid="claim-dns-verify-btn"
                        onClick={submitVerification}
                        disabled={claiming}
                        className="btn-neon rounded-full mt-2"
                      >
                        {claiming ? 'Checking…' : 'Verify DNS'}
                      </Button>
                    </div>
                  )}

                  <div className="border border-zinc-800 rounded-lg p-4 space-y-4">
                    <div>
                      <div className="font-display">Alternative verification</div>
                      <p className="text-xs text-zinc-500 mt-1">
                        Your verified TuneAmplifier account email must match the official label email.
                      </p>
                    </div>

                    <Select
                      value={claimForm.verification_method}
                      onValueChange={v => updateClaimForm('verification_method', v)}
                    >
                      <SelectTrigger data-testid="claim-verification-method" className="bg-zinc-950 border-zinc-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OFFICIAL_EMAIL">Official email matches my account</SelectItem>
                        <SelectItem value="PUBLIC_EMAIL">Email published on the official website/social</SelectItem>
                        {claimSent.dns?.record_name && (
                          <SelectItem value="DNS_TXT">DNS TXT record</SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    {claimForm.verification_method === 'OFFICIAL_EMAIL' && (
                      <Input
                        data-testid="claim-official-email"
                        type="email"
                        value={claimForm.official_email}
                        onChange={e => updateClaimForm('official_email', e.target.value)}
                        placeholder="official@yourlabel.com"
                        className="bg-zinc-950 border-zinc-800"
                      />
                    )}

                    {claimForm.verification_method === 'PUBLIC_EMAIL' && (
                      <div className="space-y-3">
                        <Input
                          data-testid="claim-public-url"
                          type="url"
                          value={claimForm.evidence_url}
                          onChange={e => updateClaimForm('evidence_url', e.target.value)}
                          placeholder="https://yourlabel.com/contact"
                          className="bg-zinc-950 border-zinc-800"
                        />
                        <Input
                          data-testid="claim-public-email"
                          type="email"
                          value={claimForm.public_email}
                          onChange={e => updateClaimForm('public_email', e.target.value)}
                          placeholder="official@yourlabel.com"
                          className="bg-zinc-950 border-zinc-800"
                        />
                      </div>
                    )}

                    <Button
                      data-testid="claim-verification-submit"
                      onClick={submitVerification}
                      disabled={claiming || claimForm.verification_method === 'DNS_TXT'}
                      className="rounded-full border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900"
                    >
                      {claiming ? 'Checking…' : 'Submit verification'}
                    </Button>
                  </div>
                </>
              )}

              {claimSent.claim?.status === 'PENDING_REVIEW' && (
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 text-sm text-zinc-300">
                  Verification passed. TuneAmplifier will review the claim before the label becomes active.
                </div>
              )}

              {['APPROVED', 'REJECTED', 'MORE_INFO', 'SUSPENDED'].includes(claimSent.claim?.status) && (
                <div className="border border-zinc-800 rounded-lg p-4 text-sm text-zinc-300">
                  Claim status: <span className="font-mono">{claimSent.claim?.status}</span>
                  {claimSent.claim?.decision_reason && (
                    <div className="mt-2 text-zinc-400">{claimSent.claim.decision_reason}</div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button
                  data-testid="claim-done-btn"
                  onClick={closeClaim}
                  className="rounded-full border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-[#ccff00]/60"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="text-xs font-mono text-zinc-500 bg-zinc-900/60 border border-zinc-800 rounded-md p-3">
                You can claim an existing label or create a new label profile. Verification is required and every new label is reviewed by TuneAmplifier.
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => updateClaimForm('mode', 'existing')}
                  className={`rounded-lg border px-3 py-2 text-xs font-mono uppercase ${
                    claimForm.mode === 'existing'
                      ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
                      : 'border-zinc-800 text-zinc-400'
                  }`}
                >
                  Existing label
                </button>
                <button
                  type="button"
                  onClick={() => updateClaimForm('mode', 'new')}
                  className={`rounded-lg border px-3 py-2 text-xs font-mono uppercase ${
                    claimForm.mode === 'new'
                      ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
                      : 'border-zinc-800 text-zinc-400'
                  }`}
                >
                  Create new label
                </button>
              </div>

              {claimForm.mode === 'existing' ? (
                <div className="space-y-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Label</label>
                    <Select value={claimForm.label_id} onValueChange={v => updateClaimForm('label_id', v)}>
                      <SelectTrigger data-testid="claim-label-select" className="mt-2 bg-zinc-950 border-zinc-800">
                        <SelectValue placeholder="Pick a label" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLabels.map(l => (
                          <SelectItem key={l.label_id} value={l.label_id}>
                            {l.name} — {l.location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {availableLabels.length === 0 && (
                      <div className="text-xs text-zinc-500 mt-2">
                        No unclaimed existing labels are available. Use Create new label.
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Your role</label>
                    <Input
                      data-testid="claim-role-input"
                      value={claimForm.role_note}
                      onChange={e => updateClaimForm('role_note', e.target.value)}
                      placeholder="e.g. Head of A&R"
                      className="mt-2 bg-zinc-950 border-zinc-800"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input
                    data-testid="claim-name-input"
                    value={claimForm.name}
                    onChange={e => updateClaimForm('name', e.target.value)}
                    placeholder="Label name"
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <MultiSelectDropdown
                      label="Music genres"
                      options={(labelFilters.genres || []).map(genre => ({
                        value: genre,
                        label: genre,
                      }))}
                      value={claimForm.genres}
                      onChange={value => updateClaimForm('genres', value)}
                      max={null}
                      placeholder="Select one or more genres"
                      testid="claim-genre-input"
                    />
                    <Input
                      data-testid="claim-location-input"
                      value={claimForm.location}
                      onChange={e => updateClaimForm('location', e.target.value)}
                      placeholder="Location"
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                  <Textarea
                    data-testid="claim-bio-input"
                    value={claimForm.bio}
                    onChange={e => updateClaimForm('bio', e.target.value)}
                    placeholder="Short label bio"
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <Input
                    data-testid="claim-website-input"
                    type="url"
                    value={claimForm.website}
                    onChange={e => updateClaimForm('website', e.target.value)}
                    placeholder="https://yourlabel.com"
                    className="bg-zinc-950 border-zinc-800"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Input value={claimForm.social_links.instagram} onChange={e => updateClaimSocial('instagram', e.target.value)} placeholder="Instagram URL" className="bg-zinc-950 border-zinc-800" />
                    <Input value={claimForm.social_links.facebook} onChange={e => updateClaimSocial('facebook', e.target.value)} placeholder="Facebook URL" className="bg-zinc-950 border-zinc-800" />
                    <Input value={claimForm.social_links.twitter} onChange={e => updateClaimSocial('twitter', e.target.value)} placeholder="X / Twitter URL" className="bg-zinc-950 border-zinc-800" />
                    <Input value={claimForm.social_links.soundcloud} onChange={e => updateClaimSocial('soundcloud', e.target.value)} placeholder="SoundCloud URL" className="bg-zinc-950 border-zinc-800" />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Input value={claimForm.spotify_id} onChange={e => updateClaimForm('spotify_id', e.target.value)} placeholder="Spotify ID" className="bg-zinc-950 border-zinc-800" />
                    <Input value={claimForm.youtube_id} onChange={e => updateClaimForm('youtube_id', e.target.value)} placeholder="YouTube ID" className="bg-zinc-950 border-zinc-800" />
                    <Input value={claimForm.soundcloud_id} onChange={e => updateClaimForm('soundcloud_id', e.target.value)} placeholder="SoundCloud ID" className="bg-zinc-950 border-zinc-800" />
                  </div>

                  <Input
                    data-testid="claim-role-input"
                    value={claimForm.role_note}
                    onChange={e => updateClaimForm('role_note', e.target.value)}
                    placeholder="Your role, e.g. Label Manager"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              )}

              <DialogFooter>
                <Button
                  data-testid="claim-submit-btn"
                  onClick={doClaim}
                  disabled={claiming}
                  className="btn-neon rounded-full"
                >
                  {claiming ? 'Submitting…' : 'Create claim'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Respond dialog */}
      <Dialog open={!!respondFor} onOpenChange={(v) => !v && setRespondFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display">Respond to "{respondFor?.demo?.song?.title}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Decision</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {['interested', 'signed', 'passed'].map(d => (
                  <button key={d} data-testid={`resp-decision-${d}`} onClick={() => setRespForm({ ...respForm, decision: d })}
                    className={`px-3 py-2 rounded-lg border text-xs font-mono uppercase transition-[background-color,border-color] ${
                      respForm.decision === d ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]' : 'border-zinc-800 text-zinc-400'
                    }`}>{d}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">Feedback (optional)</label>
                <button data-testid="ai-write-feedback" onClick={writeFeedback} disabled={aiWriting} className="text-xs font-mono text-[#ccff00] hover:underline flex items-center gap-1">
                  <Sparkle size={12} weight="fill" /> {aiWriting ? 'writing…' : 'AI write'}
                </button>
              </div>
              <Textarea data-testid="resp-feedback-input" value={respForm.feedback} onChange={e => setRespForm({ ...respForm, feedback: e.target.value })} placeholder="Short honest note — what worked, what didn't." className="bg-zinc-950 border-zinc-800 min-h-[110px]" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="send-response-btn" onClick={respond} className="btn-neon rounded-full">Send response</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
